import React, { useState } from 'react';
import { Modal, Button, Typography, Card, Tag, Space } from 'antd';
import { 
  CloseOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  FileTextOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;

// Configurable value mappings for special visualization
const VALUE_MAPPINGS = {
  true: { icon: <CheckCircleOutlined />, color: '#52c41a', text: 'True' },
  false: { icon: <CloseCircleOutlined />, color: '#ff4d4f', text: 'False' },
  'approved': { icon: <CheckCircleOutlined />, color: '#52c41a', text: 'Approved' },
  'denied': { icon: <CloseCircleOutlined />, color: '#ff4d4f', text: 'Denied' },
  'failed': { icon: <CloseCircleOutlined />, color: '#ff4d4f', text: 'Failed' },
  'success': { icon: <CheckCircleOutlined />, color: '#52c41a', text: 'Success' },
  'completed': { icon: <CheckCircleOutlined />, color: '#52c41a', text: 'Completed' },
  'pending': { icon: <FileTextOutlined />, color: '#faad14', text: 'Pending' },
  'received': { icon: <CheckCircleOutlined />, color: '#1890ff', text: 'Received' }
};

// JSON Node Component for recursive rendering
const JsonNode = ({ data, nodeKey, level = 0, isLast = true }) => {
  const [collapsed, setCollapsed] = useState(level > 2); // Auto-collapse deep levels
  
  const getDataType = (value) => {
    if (value === null) return 'null';
    if (Array.isArray(value)) return 'array';
    return typeof value;
  };

  const formatValue = (value) => {
    const dataType = getDataType(value);
    
    // Handle special value mappings
    const valueStr = String(value).toLowerCase();
    if (VALUE_MAPPINGS[valueStr]) {
      const mapping = VALUE_MAPPINGS[valueStr];
      return (
        <Space size={4}>
          <span style={{ color: mapping.color, fontSize: '14px' }}>
            {mapping.icon}
          </span>
          <Text style={{ color: mapping.color, fontWeight: 500 }}>
            {mapping.text}
          </Text>
        </Space>
      );
    }
    
    // Handle different data types
    switch (dataType) {
      case 'null':
        return <Text type="secondary" italic>null</Text>;
      case 'string':
        return <Text style={{ color: '#1890ff' }}>"{value}"</Text>;
      case 'number':
        return <Text style={{ color: '#162349' }}>{value}</Text>;
      case 'boolean':
        return VALUE_MAPPINGS[value] ? 
          formatValue(value) : 
          <Text style={{ color: value ? '#52c41a' : '#ff4d4f' }}>{String(value)}</Text>;
      default:
        return <Text>{String(value)}</Text>;
    }
  };

  const renderComplexValue = (value, key) => {
    const dataType = getDataType(value);
    const hasChildren = (dataType === 'object' && value !== null && Object.keys(value).length > 0) ||
                       (dataType === 'array' && value.length > 0);
    
    if (!hasChildren) {
      return (
        <div style={{ 
          marginLeft: `${level * 24}px`,
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center'
        }}>
          <Card 
            size="small" 
            style={{ 
              minWidth: '200px',
              backgroundColor: '#fafafa',
              border: '1px solid #e8e8e8'
            }}
          >
            <Space direction="vertical" size={0}>
              <Text strong style={{ color: '#162349' }}>{key}</Text>
              <Tag color="default">{dataType === 'array' ? 'empty array' : 'empty object'}</Tag>
            </Space>
          </Card>
        </div>
      );
    }

    const children = dataType === 'array' ? 
      value.map((item, index) => ({ key: `[${index}]`, value: item })) :
      Object.entries(value).map(([k, v]) => ({ key: k, value: v }));

    return (
      <div style={{ marginLeft: `${level * 24}px`, marginBottom: '12px' }}>
        {/* Parent Node */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
          <Button
            type="text"
            size="small"
            icon={collapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ marginRight: '8px', padding: '0', width: '20px', height: '20px' }}
          />
          <Card 
            size="small" 
            style={{ 
              minWidth: '250px',
              backgroundColor: dataType === 'array' ? '#e6f7ff' : '#f6ffed',
              border: `1px solid ${dataType === 'array' ? '#91d5ff' : '#b7eb8f'}`,
              cursor: 'pointer'
            }}
            onClick={() => setCollapsed(!collapsed)}
          >
            <Space>
              {dataType === 'array' ? 
                <FolderOutlined style={{ color: '#1890ff' }} /> : 
                <FolderOpenOutlined style={{ color: '#52c41a' }} />
              }
              <Text strong style={{ color: '#162349' }}>{key}</Text>
              <Tag color={dataType === 'array' ? 'blue' : 'green'}>
                {dataType === 'array' ? `array[${value.length}]` : `object{${Object.keys(value).length}}`}
              </Tag>
            </Space>
          </Card>
        </div>

        {/* Children Nodes */}
        {!collapsed && (
          <div style={{ 
            borderLeft: '2px solid #e8e8e8', 
            marginLeft: '10px', 
            paddingLeft: '16px' 
          }}>
            {children.map((child, index) => (
              <JsonNode
                key={`${key}-${child.key}`}
                data={child.value}
                nodeKey={child.key}
                level={level + 1}
                isLast={index === children.length - 1}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderPrimitiveValue = (value, key) => {
    return (
      <div style={{ 
        marginLeft: `${level * 24}px`,
        marginBottom: '8px',
        display: 'flex',
        alignItems: 'center'
      }}>
        <Card 
          size="small" 
          style={{ 
            minWidth: '300px',
            backgroundColor: '#ffffff',
            border: '1px solid #d9d9d9'
          }}
        >
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Text strong style={{ color: '#162349', fontSize: '13px' }}>{key}</Text>
            <div>{formatValue(value)}</div>
          </Space>
        </Card>
      </div>
    );
  };

  const dataType = getDataType(data);
  
  if (dataType === 'object' || dataType === 'array') {
    return renderComplexValue(data, nodeKey);
  } else {
    return renderPrimitiveValue(data, nodeKey);
  }
};

// Main JSON Visualizer Component
const JsonVisualizer = ({ 
  visible, 
  onClose, 
  jsonData, 
  title = "JSON Data Visualization",
  width = 1200,
  height = 800 
}) => {
  const [expandAll, setExpandAll] = useState(false);

  const handleExpandToggle = () => {
    setExpandAll(!expandAll);
    // Force re-render with new default collapsed state
    // This is a simple approach - in production you might want more sophisticated state management
  };

  if (!jsonData) {
    return null;
  }

  return (
    <Modal
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0, color: '#162349' }}>
            {title}
          </Title>
          <Space>
            <Button 
              size="small" 
              onClick={handleExpandToggle}
              style={{ color: '#162349' }}
            >
              {expandAll ? 'Collapse All' : 'Expand All'}
            </Button>
          </Space>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={width}
      style={{ top: 20 }}
      bodyStyle={{ 
        height: height, 
        overflow: 'auto',
        padding: '16px',
        backgroundColor: '#fafafa'
      }}
      closeIcon={<CloseOutlined />}
    >
      <div style={{ 
        backgroundColor: 'white',
        borderRadius: '6px',
        padding: '16px',
        minHeight: '100%'
      }}>
        <JsonNode 
          data={jsonData} 
          nodeKey="root" 
          level={0}
        />
      </div>
    </Modal>
  );
};

export default JsonVisualizer;